import { FC, useEffect, useState } from 'react';

import { Link, router, usePage } from '@inertiajs/react';
import {
    ArrowDown,
    ArrowLeft,
    ArrowUp,
    ArrowUpDown,
    CircleSlash,
    ExternalLink,
    Pencil,
    Percent,
    Tags,
    TrendingDown,
} from 'lucide-react';

import { InlineOfferEditor } from './Index/InlineOfferEditor';
import { Pagination } from './Index/Pagination';
import { AnalyticsCardsRow } from '../../../components/shared/AnalyticsCardsRow';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Image } from '../../../components/ui/Image';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/Select';
import { Routes } from '../../../models/routes';
import {
    CardmarketPricePoint,
    Offer,
    OfferProperty,
    OfferPriceAnalysisRow,
    OfferPriceAnalysisSummary,
} from '../../../types';
import { cn } from '../../../utils';

type PageProps = {
    rows: OfferPriceAnalysisRow[];
    offerProperties: OfferProperty[];
    nextPage: number | null;
    sort: SortKey | null;
    direction: SortDirection;
    summary: OfferPriceAnalysisSummary;
    exchangeRates: Record<string, string | null>;
    exchangeRateErrors: Record<string, string>;
    analysisPending: boolean;
    analysisUpdatedAt: string | null;
    q: AnalyticsQuery | null;
    filterUniverses: FilterOption[] | null;
    filterProductTypes: FilterOption[] | null;
};

type SortKey = 'price' | CardmarketPricePoint['key'];
type SortDirection = 'asc' | 'desc';
type AnalyticsQuery = {
    universe_slug?: string;
    product_type_slug?: string;
};
type FilterOption = {
    id: number;
    name: string;
    slug: string;
};

const ALL_FILTER_VALUE = 'all';

const PRICE_COLUMNS: Array<{
    key: CardmarketPricePoint['key'];
    label: string;
}> = [
    { key: 'trend', label: 'Trend' },
    { key: 'avg7', label: '7 days' },
    { key: 'avg30', label: '30 days' },
    { key: 'avg', label: 'Average' },
];

const formatUah = (value: number | null | undefined) => {
    if (value == null) {
        return '—';
    }

    return new Intl.NumberFormat('uk-UA', {
        style: 'currency',
        currency: 'UAH',
        maximumFractionDigits: 0,
    }).format(value);
};

const formatCurrencyAmount = (price: CardmarketPricePoint) =>
    new Intl.NumberFormat('uk-UA', {
        style: 'currency',
        currency: price.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(price.amount));

const formatPercent = (value: number | null) => {
    if (value == null) {
        return '—';
    }

    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
};

const deviationPercent = (
    offerPriceUah: number,
    marketPriceUah: number | null | undefined,
) => {
    if (!marketPriceUah || marketPriceUah <= 0) {
        return null;
    }

    return Number(
        (((offerPriceUah - marketPriceUah) / marketPriceUah) * 100).toFixed(1),
    );
};

const formatDate = (value: string | null) => {
    if (!value) {
        return null;
    }

    return new Intl.DateTimeFormat('uk-UA', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(new Date(value));
};

const formatDateTime = (value: string) =>
    new Intl.DateTimeFormat('uk-UA', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(value));

const deviationTone = (value: number | null) => {
    if (value == null) {
        return {
            label: 'Без порівняння',
            className:
                'border-slate-200 bg-slate-100 text-slate-700 dark:border-border dark:bg-slate-800/70 dark:text-slate-300',
            icon: CircleSlash,
        };
    }

    if (value < 0) {
        return {
            label: 'Нижче ринку',
            className:
                'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300',
            icon: ArrowDown,
        };
    }

    if (value > 0) {
        return {
            label: 'Вище ринку',
            className:
                'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300',
            icon: ArrowUp,
        };
    }

    return {
        label: 'Ринкова ціна',
        className:
            'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300',
        icon: Percent,
    };
};

const priceByKey = (
    prices: CardmarketPricePoint[],
    key: CardmarketPricePoint['key'],
) => prices.find((price) => price.key === key) ?? null;

const cleanAnalyticsQuery = (query: AnalyticsQuery): AnalyticsQuery =>
    Object.fromEntries(
        Object.entries(query).filter(
            ([, value]) => Boolean(value) && value !== ALL_FILTER_VALUE,
        ),
    ) as AnalyticsQuery;

const SortHeader: FC<{
    label: string;
    sortKey: SortKey;
    currentSort: SortKey | null;
    currentDirection: SortDirection;
    onSortChange: (sortKey: SortKey) => void;
}> = ({ label, sortKey, currentSort, currentDirection, onSortChange }) => {
    const active = currentSort === sortKey;
    const Icon = (() => {
        if (!active) {
            return ArrowUpDown;
        }

        if (sortKey === 'price') {
            return currentDirection === 'asc' ? ArrowUp : ArrowDown;
        }

        return currentDirection === 'desc' ? ArrowUp : ArrowDown;
    })();

    return (
        <button
            type="button"
            className={cn(
                'ml-auto inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold transition-colors hover:text-foreground',
                active ? 'text-foreground' : 'text-muted-foreground',
            )}
            onClick={() => onSortChange(sortKey)}
            aria-label={`Сортувати ${label}`}
        >
            {label}
            <Icon className="h-3.5 w-3.5" />
        </button>
    );
};

const PriceCell: FC<{
    label: string;
    offerPrice: number;
    price: CardmarketPricePoint | null;
}> = ({ label, offerPrice, price }) => {
    const percent = deviationPercent(offerPrice, price?.uah);
    const tone = deviationTone(percent);
    const Icon = tone.icon;

    return (
        <div className="flex min-w-0 items-center justify-between gap-3 md:block md:text-right">
            <span className="text-xs font-medium text-muted-foreground md:hidden">
                {label}
            </span>
            {price ? (
                <div className="flex min-w-0 flex-col items-end gap-1">
                    <div className="text-sm font-semibold">
                        {formatCurrencyAmount(price)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {formatUah(price.uah)}
                    </div>
                    {percent != null && (
                        <Badge
                            variant="outline"
                            className={cn(
                                'gap-1 border px-1.5 py-0 text-[11px]',
                                tone.className,
                            )}
                        >
                            <Icon className="h-3 w-3" />
                            {formatPercent(percent)}
                        </Badge>
                    )}
                </div>
            ) : (
                <span className="text-sm text-muted-foreground">—</span>
            )}
        </div>
    );
};

const ProductCell: FC<{ row: OfferPriceAnalysisRow }> = ({ row }) => {
    const priceDate = formatDate(row.price_date);

    return (
        <div className="flex min-w-0 gap-3">
            <Link
                href={row.product.url_path}
                className="relative h-16 w-12 shrink-0 overflow-hidden rounded-md bg-muted"
            >
                <Image
                    src={row.product.image}
                    alt={row.product.image_alt}
                    className="object-cover"
                />
            </Link>
            <div className="min-w-0">
                <Link
                    href={row.product.url_path}
                    className="line-clamp-2 text-sm font-semibold hover:text-primary"
                >
                    {row.product.name}
                </Link>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className="px-1.5 text-[11px]">
                        {row.product.expansion_tag}
                    </Badge>
                    <Badge variant="secondary" className="px-1.5 text-[11px]">
                        {row.product.language}
                    </Badge>
                    <Badge
                        variant={
                            row.status === 'active' ? 'success' : 'neutral'
                        }
                        className="px-1.5 text-[11px]"
                    >
                        {row.status === 'active' ? 'Активна' : 'Призупинена'}
                    </Badge>
                </div>
                {priceDate && (
                    <p className="mt-1 text-xs text-muted-foreground">
                        Cardmarket: {priceDate}
                    </p>
                )}
            </div>
        </div>
    );
};

const EmptyState: FC = () => (
    <div className="rounded-2xl border bg-muted/10 py-12 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <TrendingDown className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="mb-1 text-lg font-medium">Немає пропозицій</h3>
        <p className="mb-4 text-sm text-muted-foreground">
            Створіть пропозицію, щоб побачити порівняння з Cardmarket.
        </p>
        <Button asChild>
            <Link href={Routes.NewOffer}>Створити пропозицію</Link>
        </Button>
    </div>
);

const PendingState: FC = () => (
    <div className="rounded-2xl border bg-muted/10 py-12 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <TrendingDown className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="mb-1 text-lg font-medium">Аналіз оновлюється</h3>
        <p className="text-sm text-muted-foreground">
            Розрахунок виконується у фоні. Оновіть сторінку за кілька хвилин.
        </p>
    </div>
);

const PriceAnalysis: FC = () => {
    const {
        rows: initialRows,
        offerProperties,
        nextPage,
        sort,
        direction,
        summary,
        exchangeRates,
        exchangeRateErrors,
        analysisPending,
        analysisUpdatedAt,
        q,
        filterUniverses,
        filterProductTypes,
    } = usePage<PageProps>().props;
    const [rows, setRows] = useState(initialRows);
    const [editingOfferId, setEditingOfferId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const exchangeRateEntries = Object.entries(exchangeRates).filter(
        ([, rate]) => rate,
    );
    const exchangeErrorEntries = Object.values(exchangeRateErrors);
    const currentQuery = q ?? {};
    const selectedUniverseSlug = currentQuery.universe_slug ?? ALL_FILTER_VALUE;
    const selectedProductTypeSlug =
        currentQuery.product_type_slug ?? ALL_FILTER_VALUE;
    const universeOptions = filterUniverses ?? [];
    const productTypeOptions = filterProductTypes ?? [];
    const productTypeFilterDisabled =
        selectedUniverseSlug === ALL_FILTER_VALUE ||
        productTypeOptions.length === 0;

    useEffect(() => {
        setRows(initialRows);
    }, [initialRows]);

    const updateRowOffer = (updatedOffer: Offer) => {
        setRows((currentRows) =>
            currentRows.map((row) =>
                row.id === updatedOffer.id
                    ? {
                          ...row,
                          ...updatedOffer,
                      }
                    : row,
            ),
        );
    };
    const analyticsParams = ({
        page,
        append,
        nextSort = sort,
        nextDirection = direction,
        nextQuery = currentQuery,
    }: {
        page?: number;
        append?: boolean;
        nextSort?: SortKey | null;
        nextDirection?: SortDirection;
        nextQuery?: AnalyticsQuery;
    }) => ({
        ...(page ? { page } : {}),
        ...(append ? { append: true } : {}),
        ...(nextSort ? { sort: nextSort } : {}),
        direction: nextDirection,
        q: cleanAnalyticsQuery(nextQuery),
    });
    const requestAnalytics = (nextQuery: AnalyticsQuery) => {
        router.get(Routes.OffersAnalytics, analyticsParams({ nextQuery }), {
            preserveScroll: true,
            preserveState: true,
            onStart: () => setLoading(true),
            onFinish: () => setLoading(false),
        });
    };
    const handleUniverseChange = (value: string) => {
        requestAnalytics(
            value === ALL_FILTER_VALUE ? {} : { universe_slug: value },
        );
    };
    const handleProductTypeChange = (value: string) => {
        const nextUniverseSlug =
            selectedUniverseSlug === ALL_FILTER_VALUE
                ? undefined
                : selectedUniverseSlug;

        requestAnalytics({
            ...(nextUniverseSlug ? { universe_slug: nextUniverseSlug } : {}),
            ...(value === ALL_FILTER_VALUE ? {} : { product_type_slug: value }),
        });
    };
    const loadMoreRows = (page: number) => {
        router.get(
            Routes.OffersAnalytics,
            analyticsParams({ page, append: true }),
            {
                preserveScroll: true,
                preserveState: true,
                preserveUrl: true,
                only: ['rows', 'nextPage'],
                onStart: () => setLoading(true),
                onFinish: () => setLoading(false),
            },
        );
    };
    const changeSort = (nextSort: SortKey) => {
        const nextDirection =
            sort === nextSort && direction === 'asc' ? 'desc' : 'asc';

        router.get(
            Routes.OffersAnalytics,
            analyticsParams({ nextSort, nextDirection }),
            {
                preserveScroll: true,
                preserveState: true,
                onStart: () => setLoading(true),
                onFinish: () => setLoading(false),
            },
        );
    };

    return (
        <div className="py-6">
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="mb-1 -ml-3"
                    >
                        <Link href={Routes.Offers}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Пропозиції
                        </Link>
                    </Button>
                    <h1 className="text-2xl font-bold sm:text-3xl">
                        Аналіз цін
                    </h1>
                </div>
                <Button asChild className="w-full sm:w-auto">
                    <Link href={Routes.NewOffer}>Створити пропозицію</Link>
                </Button>
            </div>

            <AnalyticsCardsRow
                items={[
                    {
                        label: 'Всього',
                        value: summary.total,
                        icon: Tags,
                        iconWrapperClassName: 'bg-primary/10',
                        iconClassName: 'text-primary',
                    },
                    {
                        label: 'Нижче ринку',
                        value: summary.below_market,
                        icon: ArrowDown,
                        iconWrapperClassName: 'bg-amber-500/10',
                        iconClassName: 'text-amber-500',
                    },
                    {
                        label: 'Вище ринку',
                        value: summary.above_market,
                        icon: ArrowUp,
                        iconWrapperClassName: 'bg-emerald-500/10',
                        iconClassName: 'text-emerald-500',
                    },
                    {
                        label: 'Середнє відхилення',
                        value: formatPercent(summary.average_deviation_percent),
                        icon: Percent,
                        iconWrapperClassName: 'bg-blue-500/10',
                        iconClassName: 'text-blue-500',
                    },
                    {
                        label: 'Без ціни',
                        value: summary.without_market_price,
                        icon: CircleSlash,
                        iconWrapperClassName: 'bg-slate-500/10',
                        iconClassName: 'text-slate-500',
                    },
                ]}
            />

            {(analysisPending || analysisUpdatedAt) && (
                <div className="mb-4 flex flex-col gap-2 rounded-xl border bg-muted/20 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-muted-foreground">
                        {analysisPending
                            ? 'Нові пропозиції ще обробляються у фоні.'
                            : 'Аналіз розраховано за останніми імпортованими цінами.'}
                    </span>
                    {analysisUpdatedAt && (
                        <span className="font-medium whitespace-nowrap">
                            Оновлено: {formatDateTime(analysisUpdatedAt)}
                        </span>
                    )}
                </div>
            )}

            {(universeOptions.length > 0 ||
                selectedUniverseSlug !== ALL_FILTER_VALUE ||
                exchangeRateEntries.length > 0 ||
                exchangeErrorEntries.length > 0) && (
                <div className="mb-4 flex flex-wrap items-center gap-2">
                    {(universeOptions.length > 0 ||
                        selectedUniverseSlug !== ALL_FILTER_VALUE) && (
                        <>
                            <Select
                                value={selectedUniverseSlug}
                                onValueChange={handleUniverseChange}
                            >
                                <SelectTrigger className="h-9 w-full text-sm sm:w-[300px] [&_svg]:h-3.5 [&_svg]:w-3.5">
                                    <SelectValue placeholder="Всі ігри" />
                                </SelectTrigger>
                                <SelectContent className="text-sm">
                                    <SelectItem
                                        className="py-1 text-sm"
                                        value={ALL_FILTER_VALUE}
                                    >
                                        Всі ігри
                                    </SelectItem>
                                    {universeOptions.map((universe) => (
                                        <SelectItem
                                            key={universe.id}
                                            className="py-1 text-sm"
                                            value={universe.slug}
                                        >
                                            {universe.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <Select
                                value={selectedProductTypeSlug}
                                onValueChange={handleProductTypeChange}
                                disabled={productTypeFilterDisabled}
                            >
                                <SelectTrigger className="h-9 w-full text-sm sm:w-[260px] [&_svg]:h-3.5 [&_svg]:w-3.5">
                                    <SelectValue placeholder="Всі типи" />
                                </SelectTrigger>
                                <SelectContent className="text-sm">
                                    <SelectItem
                                        className="py-1 text-sm"
                                        value={ALL_FILTER_VALUE}
                                    >
                                        Всі типи
                                    </SelectItem>
                                    {productTypeOptions.map((productType) => (
                                        <SelectItem
                                            key={productType.id}
                                            className="py-1 text-sm"
                                            value={productType.slug}
                                        >
                                            {productType.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </>
                    )}
                    {exchangeRateEntries.map(([currency, rate]) => (
                        <span
                            key={currency}
                            className="flex h-9 items-center rounded-xl border bg-background px-3 text-sm font-semibold whitespace-nowrap text-muted-foreground"
                        >
                            {currency}/UAH: {Number(rate).toFixed(2)}
                        </span>
                    ))}
                    {exchangeErrorEntries.map((error) => (
                        <span
                            key={error}
                            className="flex h-9 items-center rounded-xl border border-amber-200 bg-amber-50 px-3 text-sm font-semibold whitespace-nowrap text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300"
                        >
                            {error}
                        </span>
                    ))}
                </div>
            )}

            {rows.length === 0 ? (
                analysisPending ? (
                    <PendingState />
                ) : (
                    <EmptyState />
                )
            ) : (
                <div className="overflow-x-auto rounded-2xl border">
                    <div className="hidden min-w-[980px] grid-cols-[minmax(240px,1.7fr)_110px_repeat(4,120px)_88px] gap-4 bg-muted/60 px-4 py-3 text-xs font-semibold text-muted-foreground md:grid">
                        <div>Пропозиція</div>
                        <SortHeader
                            label="Ваша ціна"
                            sortKey="price"
                            currentSort={sort}
                            currentDirection={direction}
                            onSortChange={changeSort}
                        />
                        {PRICE_COLUMNS.map((column) => (
                            <div key={column.key} className="text-right">
                                <SortHeader
                                    label={column.label}
                                    sortKey={column.key}
                                    currentSort={sort}
                                    currentDirection={direction}
                                    onSortChange={changeSort}
                                />
                            </div>
                        ))}
                        <div className="text-right">Дії</div>
                    </div>

                    <div className="divide-y">
                        {rows.map((row) => {
                            const isEditing = editingOfferId === row.id;

                            return (
                                <div
                                    key={row.id}
                                    data-testid="price-analysis-row"
                                    className="bg-background transition-colors hover:bg-muted/35"
                                >
                                    <div className="grid gap-4 px-3 py-4 md:min-w-[980px] md:grid-cols-[minmax(240px,1.7fr)_110px_repeat(4,120px)_88px] md:items-center md:px-4">
                                        <ProductCell row={row} />

                                        <div className="flex items-center justify-between md:block md:text-right">
                                            <span className="text-xs font-medium text-muted-foreground md:hidden">
                                                Ваша ціна
                                            </span>
                                            <div>
                                                <div className="text-base font-bold">
                                                    {formatUah(row.price)}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {row.amount} шт.
                                                </div>
                                            </div>
                                        </div>

                                        {PRICE_COLUMNS.map((column) => (
                                            <PriceCell
                                                key={column.key}
                                                label={column.label}
                                                offerPrice={row.price}
                                                price={priceByKey(
                                                    row.cardmarket_prices,
                                                    column.key,
                                                )}
                                            />
                                        ))}

                                        <div className="flex justify-end gap-2">
                                            <Button
                                                asChild
                                                variant="outline"
                                                size="icon"
                                                className="h-8 w-8"
                                                aria-label="Відкрити продукт"
                                            >
                                                <Link
                                                    href={row.product.url_path}
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                </Link>
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={
                                                    isEditing
                                                        ? 'outlinePrimary'
                                                        : 'outline'
                                                }
                                                size="icon"
                                                className="h-8 w-8"
                                                aria-label="Редагувати пропозицію"
                                                aria-expanded={isEditing}
                                                onClick={() =>
                                                    setEditingOfferId(
                                                        isEditing
                                                            ? null
                                                            : row.id,
                                                    )
                                                }
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                    {isEditing && (
                                        <div className="border-t bg-muted/20 p-3 sm:p-4">
                                            <InlineOfferEditor
                                                offer={row}
                                                offerProperties={
                                                    offerProperties
                                                }
                                                onCancel={() =>
                                                    setEditingOfferId(null)
                                                }
                                                onUpdatedOffer={updateRowOffer}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            <div className="mt-4">
                <Pagination
                    nextPage={nextPage}
                    loading={loading}
                    onLoadMore={loadMoreRows}
                />
            </div>
        </div>
    );
};

export default PriceAnalysis;
